import {useBizFileUrlsResolver} from "../../../hooks/use-biz-file-urls-resolver";
import React, {cloneElement, useCallback, useMemo, useState} from "react";
import {BizFormQuestionTypeIds} from "../../../../packages/biz-form";
import UIUtils from "../../../utils";
import FilePreviewModal, {FilePreviewTypes} from "../file-preview";
import {Box, Skeleton, Typography} from "@mui/material";
import LazyImage from "../../app-specific/lazy-image";
import DateUtils from "../../../../core/services/date-utils";

export const QuestionAnswer = ({question, answer, loading, getFilePreviewUrl: getFilePreviewUrlProp}) => {
    const ncrBizFile = useBizFileUrlsResolver((t) => t.ncrBizForm);
    const getFilePreviewUrl = useCallback(
        (guid) => (getFilePreviewUrlProp ? getFilePreviewUrlProp(guid) : ncrBizFile.getFilePreviewUrl(guid)),
        [getFilePreviewUrlProp, ncrBizFile],
    );
    const [previewDialog, setPreviewDialog] = useState(false);
    const isImage = useMemo(() => {
        return question?.questionTypeId === BizFormQuestionTypeIds.image ||
            (question?.questionTypeId === BizFormQuestionTypeIds.file && UIUtils.isImage(answer?.content?.split('.').pop()));
    }, [question?.questionTypeId, answer?.content])
    const isVideo = useMemo(() => {
        return (question?.questionTypeId === BizFormQuestionTypeIds.file && UIUtils.isVideo(answer?.content?.split('.').pop()));
    }, [question?.questionTypeId, answer?.content])

    /**
     * Previews the file answer if the question type is file or image.
     * @type {(function(): void)|*}
     */
    const previewFile = useCallback(() => {
        if (!isImage && !isVideo) {
            window.open(getFilePreviewUrl(answer?.content));
        } else {
            setPreviewDialog(true);
        }
    }, [answer?.content, isImage, isVideo, getFilePreviewUrl])

    const previewData = useMemo(() => {
        return ({
            url: getFilePreviewUrl(answer?.content),
            fileName: answer?.content ?? "",
            type: isImage ? FilePreviewTypes.image : isVideo ? FilePreviewTypes.video : FilePreviewTypes.unknown,
        });
    }, [answer?.content, isImage, isVideo, getFilePreviewUrl])

    const answerText = useMemo(() => {
        if (loading) {
            return null;
        }
        if (isImage) {
            const src = getFilePreviewUrl(answer?.content);
            if (!src || !answer?.content) {
                return (
                    <Typography variant={'caption'}>
                        No image
                    </Typography>
                );
            }
            return (
                <LazyImage
                    src={getFilePreviewUrl(answer?.content)}
                    alt={question?.content}
                    style={{maxHeight: 120, width: 'min(400px, 100%)', objectFit: 'contain', objectPosition: 'center', display: 'flex', marginInline: 'auto', borderRadius: '10px'}}
                    onClick={() => previewFile()}
                />
            )
        }
        if (question?.questionTypeId === BizFormQuestionTypeIds.file) {
            if (!answer?.content) {
                return (
                    <Typography variant={'caption'}>
                        No file
                    </Typography>
                );
            }
            return cloneElement(
                UIUtils.getFileIcon(answer?.content?.split('.').pop()),
                {
                    style: {width: 60, height: 60},
                    onClick: () => previewFile(),
                }
            )
        }
        let text = '';
        switch (question?.questionTypeId) {
            case BizFormQuestionTypeIds.shortAnswer:
            case BizFormQuestionTypeIds.longAnswer:
                text = answer?.content ?? "";
                break;
            case BizFormQuestionTypeIds.select:
            case BizFormQuestionTypeIds.trueFalse: {
                const answerOptionIds = answer?.optionResponses?.map(e => e.optionId ?? e.id) ?? [];
                text = question?.options?.find((option) => answerOptionIds.includes(option.optionId ?? option.id))?.content ?? '';
                break;
            }
            case BizFormQuestionTypeIds.multiSelect: {
                const answerOptionIds = answer?.optionResponses?.map(e => e.optionId ?? e.id) ?? [];
                text = question?.options?.filter((option) => answerOptionIds.includes(option.optionId ?? option.id))?.map(e => e.content)?.join(', ') ?? '';
                break;
            }
            case BizFormQuestionTypeIds.date:
                text = answer?.content ? DateUtils.serverToLocal(answer.content).format(DateUtils.dateFormat) : ''
                break;
            case BizFormQuestionTypeIds.dateTime:
                text = answer?.content ? DateUtils.serverToLocal(answer.content).format(DateUtils.dateTimeFormat) : '';
                break;
            case BizFormQuestionTypeIds.time:
                text = answer?.content ? DateUtils.serverToLocal(answer.content).format(DateUtils.timeFormat) : ''
                break;
            default:
                break;
        }
        return (
            <Typography variant={'caption'}>
                {!!text?.trim() ? text : '--'}
            </Typography>
        );
    }, [answer?.content, answer?.optionResponses, loading, question?.content, question?.options, question?.questionTypeId, isImage, previewFile, getFilePreviewUrl])

    if (loading) {
        return (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                <Skeleton variant={'rounded'} width={'50%'} height={20}/>
                <Skeleton variant={'rounded'} width={'100%'} height={50}/>
            </Box>
        );
    }
    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                <Typography variant={'body2'} fontWeight={600}>
                    {question?.content}
                </Typography>
                {
                    answerText
                }
            </Box>
            <FilePreviewModal
                data={previewData}
                open={previewDialog}
                fetchByOpenNewTab
                setOpen={() => setPreviewDialog(false)}
            />
        </>
    );
}